/**
 * @file StepTransition — exit→enter step animation orchestrator
 * @version 0.1.0
 */

import React, {
  forwardRef,
  useEffect,
  useState,
  type ElementType,
  type HTMLAttributes,
  type ReactNode,
} from 'react';

/*
 * StepTransition — generic exit→enter step orchestrator.
 *
 * Controlled by `index`. When it changes, StepTransition keeps the outgoing
 * step mounted with `phase="exit"` while the incoming mounts with
 * `phase="enter"`, both overlapping in a single grid cell so they animate past
 * each other. After `durationMs` the outgoing unmounts and the current settles
 * to `phase="rest"`.
 *
 * It owns ONLY the overlap + timing + remount mechanics — it carries no motion
 * of its own. The child decides how to animate from the `phase`/`direction` it
 * receives (e.g. by applying the brand's hd-q-* classes). The host owns the
 * sequence: its own index state, the data, and the navigation.
 *
 *   <StepTransition index={i} durationMs={1250}>
 *     {({ index, phase, direction }) => (
 *       <Question {...questions[index]} phase={phase} direction={direction} />
 *     )}
 *   </StepTransition>
 */

export type StepPhase = 'rest' | 'enter' | 'exit';

export interface StepRenderArgs {
  /** The index of the step being rendered (incoming = current; outgoing = previous). */
  index: number;
  /** Apply this to the content's animated parts. `rest` = settled, no animation. */
  phase: StepPhase;
  /** 1 = forward (next), -1 = backward (previous). */
  direction: 1 | -1;
}

export interface StepTransitionProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Current step index. Changing it runs the exit→enter transition. */
  index: number;
  /** How long the outgoing step stays mounted (ms) — set to the longest
   *  exit-then-enter chain the children animate. Default 1250. */
  durationMs?: number;
  exitMs?: number;
  /** Render a step from its index + the phase/direction to apply. Called for the
   *  incoming step always, and for the outgoing step during a transition. */
  children: (args: StepRenderArgs) => ReactNode;
  /** Render the root as a different element/component. Default `'div'`. */
  as?: ElementType;
}

/* Reduced motion collapses the children's exits to a short fade, so the
 * outgoing step can leave the layout almost immediately. */
const REDUCED_MOTION_EXIT_MS = 150;

interface TransitionState {
  /** Distinguishes one transition from the next, so a reversal mid-flight
   *  reschedules timers instead of letting stale ones fire. */
  id: number;
  outgoingIndex: number;
  direction: 1 | -1;
  /** True until `exitMs` — while true, the outgoing step occupies the cell. */
  outgoingInLayout: boolean;
}

export const StepTransition = forwardRef<HTMLDivElement, StepTransitionProps>(
  function StepTransition(
    { index, durationMs = 1250, exitMs = 500, children, className, style, as, ...rest },
    ref,
  ) {
  const Component = as ?? 'div';
  const [transition, setTransition] = useState<TransitionState | null>(null);
  const [prevIndex, setPrevIndex] = useState(index);

  // Start the transition during render when the controlled index changes — the
  // React-sanctioned "adjust state on a prop change" pattern (state, not a ref,
  // so StrictMode's double render is safe). The outgoing step mounts in the same
  // commit, so there's no settled-state flash before it slides.
  if (index !== prevIndex) {
    setTransition((t) => ({
      id: (t?.id ?? 0) + 1,
      outgoingIndex: prevIndex,
      direction: index > prevIndex ? 1 : -1,
      outgoingInLayout: true,
    }));    
    setPrevIndex(index);
  }

  const transitionId = transition?.id;

  useEffect(() => {
  if (transitionId === undefined) return;
  const reduced =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const exitAt = Math.min(reduced ? REDUCED_MOTION_EXIT_MS : exitMs, durationMs);
    // Two clocks on purpose: the outgoing leaves the LAYOUT at exitAt (page
    // height settles early, in the beat where the incoming is still invisible),
    // while the incoming keeps its enter phase until durationMs (choreography
    // plays uncut).
  const exitTimer = window.setTimeout(
    () =>
      setTransition((t) =>
        t && t.id === transitionId ? { ...t, outgoingInLayout: false } : t,
      ),
    exitAt,
  );
  const settleTimer = window.setTimeout(
    () => setTransition((t) => (t && t.id === transitionId ? null : t)),
    durationMs,
  );
  return () => {
    window.clearTimeout(exitTimer);
    window.clearTimeout(settleTimer);
  };
  }, [transitionId, durationMs, exitMs]);

  return (  
    <Component
      ref={ref}
      className={className}
      style={{
        position: 'relative',
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr)',
        gridTemplateRows: 'minmax(0, 1fr)',
        minHeight: 0,
        ...style,
      }}
      {...rest}
    >
      {/* Both slots are keyed by their STEP INDEX (not an in/out role prefix) so
       *  that when the current step becomes the outgoing one, React preserves
       *  the SAME instance — it slides out carrying its own state (e.g. a
       *  question's picked answer) instead of remounting blank. The incoming is
       *  always a new index → new key → fresh mount, so enter animations still
       *  re-fire. Do NOT reintroduce distinct out-/in- prefixes: that forces the
       *  outgoing to remount and wipes its in-progress state mid-slide. */}
      {transition?.outgoingInLayout && (
        <div
          key={`step-${transition.outgoingIndex}`}
          style={{ gridArea: '1 / 1', minHeight: 0, pointerEvents: 'none' }}
        >
          {children({ index: transition.outgoingIndex, phase: 'exit', direction: transition.direction })}
        </div>
      )}
      <div key={`step-${index}`} style={{ gridArea: '1 / 1', minHeight: 0 }}>
        {children({
          index,
          phase: transition ? 'enter' : 'rest',
          direction: transition?.direction ?? 1,
        })}
      </div>
    </Component>
  );
  },
);

StepTransition.displayName = 'StepTransition';
